/**
 * CertificateVerify — public verifiable certificate landing.
 *
 * URL: /certificates/verify/:certId — anyone can hit it. Honest-date.
 * Revoked certs render the revoke reason transparently.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Award, AlertTriangle, CheckCircle2, Circle, Loader2, ExternalLink, Download } from 'lucide-react';
import api from '../lib/api';
import { apiBase } from '../lib/absoluteMediaUrl';

export default function CertificateVerify() {
  const { certId } = useParams();
  const [cert, setCert] = useState(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/certificates/verify/${certId}`);
        setCert(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Certificate not found.');
      }
      setBusy(false);
    })();
  }, [certId]);

  const pdfUrl = cert ? `${apiBase()}/api/certificates/${cert.certificate_id}/pdf` : '';

  return (
    <div className="min-h-screen px-4 py-10" style={{ background: '#0A0A0A' }} data-testid="cert-verify-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <Award size={16} className="text-[#D4A84A]" />
          <span className="font-hud text-[10px] md:text-xs text-[#D4A84A] tracking-widest uppercase">TSOGN — Public Verification</span>
        </div>
        {busy && (
          <div className="flex items-center gap-3 font-mono text-sm text-white/85 mt-6">
            <Loader2 size={16} className="animate-spin text-[#2F8A92]" />
            Verifying certificate…
          </div>
        )}
        {!busy && error && (
          <div className="border border-[#FF6B6B]/30 rounded p-6 bg-[#FF6B6B]/5 mt-6" data-testid="cert-verify-error">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={18} className="text-[#FF6B6B]" />
              <span className="font-historical text-xl text-[#FF6B6B] font-light">Not found</span>
            </div>
            <p className="font-mono text-sm text-white/85">{error}</p>
          </div>
        )}
        {!busy && cert && (
          <div data-testid="cert-verify-card">
            <h1 className="font-historical text-[#D4A84A] text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mt-2 mb-1">
              Foundation Tier
            </h1>
            <p className="font-mono text-sm md:text-base text-white/85 mb-6">
              Awarded to <strong className="text-white">{cert.user_name || 'TSOGN Member'}</strong> on {new Date(cert.issued_at).toLocaleDateString()}.
            </p>

            {cert.revoked_at && (
              <div className="border border-[#FF6B6B]/40 rounded p-4 md:p-5 bg-[#FF6B6B]/8 mb-6" data-testid="cert-verify-revoked">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={18} className="text-[#FF6B6B]" />
                  <span className="font-historical text-xl text-[#FF6B6B] font-light">REVOKED</span>
                </div>
                <p className="font-mono text-sm text-white/85 leading-relaxed">
                  Revoked {new Date(cert.revoked_at).toLocaleDateString()}.
                </p>
                {cert.revoke_reason && (
                  <p className="font-mono text-xs text-white/65 mt-2 leading-relaxed">
                    Reason: {cert.revoke_reason}
                  </p>
                )}
              </div>
            )}

            <div className="border border-[#D4A84A]/15 rounded p-4 md:p-5 mb-6">
              <div className="font-hud text-[10px] md:text-xs text-[#D4A84A] tracking-widest uppercase mb-3">Criteria met</div>
              <ul className="space-y-2">
                {(cert.criteria_definitions || []).map((d) => {
                  const met = !!cert.criteria_met?.[d.key];
                  return (
                    <li key={d.key} className="flex items-start gap-3">
                      {met ? (
                        <CheckCircle2 size={15} className="text-[#2F8A92] mt-0.5 flex-shrink-0" />
                      ) : (
                        <Circle size={15} className="text-white/25 mt-0.5 flex-shrink-0" />
                      )}
                      <div className={`font-mono text-xs md:text-sm ${met ? 'text-white/85' : 'text-white/55'}`}>{d.label}</div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="flex flex-wrap gap-2">
              <a
                href={pdfUrl}
                target="_blank" rel="noopener noreferrer"
                data-testid="cert-verify-pdf-link"
                className="font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-3 py-2 hover:bg-[#D4A84A]/85 inline-flex items-center gap-1.5"
              >
                <Download size={12} /> DOWNLOAD PDF
              </a>
              <a
                href="/"
                className="font-hud text-xs tracking-widest text-white/85 border border-[#2F8A92]/40 px-3 py-2 hover:text-[#2F8A92] inline-flex items-center gap-1.5"
              >
                <ExternalLink size={12} /> ENTER TSOGN
              </a>
            </div>

            <p className="font-mono text-[10px] text-white/35 mt-6 leading-relaxed">
              Honest-date principle · Methodology hold #4 — documented and verifiable. Certificate ID: {cert.certificate_id}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
